import { blogPosts } from './content'

export type Author = {
  slug: string
  name: string
  /** Role, bio, photo, and socials are provided by the team (the founders come
   * from the About page); left undefined when we do not have them yet. */
  role?: string
  bio?: string
  photo?: string
  linkedin?: string
  github?: string
}

const FRAMER = 'https://framerusercontent.com/images'

const AUTHORS: Author[] = [
  {
    slug: 'alex-forbes-reed',
    name: 'Alex Forbes-Reed',
    role: 'OpenTelemetry Architect',
    bio: 'OpenTelemetry architect and expert, leading observability and instrumentation work at LangWatch.',
    photo: `${FRAMER}/9ARqZK3lvVyO3HLQbIHc3dH6sc8.png`,
    linkedin: 'https://www.linkedin.com/in/alexforbesreed/',
    github: 'https://github.com/0xdeafcafe',
  },
  {
    slug: 'andrew-joia',
    name: 'Andrew Garde Joia',
    role: 'AI Reliability Engineer',
    bio: 'Former startup founder and Yale alum, now an AI reliability expert working on agent testing and evaluation.',
    photo: '/people/andrew-joia.webp',
    linkedin: 'https://fr.linkedin.com/in/andrewgardejoia',
    github: 'https://github.com/drewdrewthis',
  },
  {
    slug: 'aryan-sharma',
    name: 'Aryan Sharma',
    role: 'Engineer',
    bio: 'The Gen Z of the team, working on agent testing, red teaming, and developer tooling.',
    photo: `${FRAMER}/Y96qMmCKGpN0cihRyPzeqlPGDM.jpg`,
    github: 'https://github.com/Aryansharma28',
  },
  {
    slug: 'bram-p',
    name: 'Bram P',
    photo: `${FRAMER}/OB3BklBp0JroME555CsLtUed5uA.jpg`,
  },
  {
    slug: 'manouk-draisma',
    name: 'Manouk Draisma',
    role: 'Co-founder & CEO',
    bio: 'Commercial leadership at Lightspeed and Biller before LangWatch. Met Rogerio at Antler Amsterdam, where the GenAI side-projects they were both working on turned out to share one missing piece: quality control. Runs go-to-market and the team.',
    photo: '/people/manouk-draisma.jpeg',
    linkedin: 'https://www.linkedin.com/in/manoukdraisma/',
  },
  { slug: 'richard', name: 'Richard' },
  {
    slug: 'rogerio-chaves',
    name: 'Rogerio Chaves',
    role: 'Co-founder & CTO',
    bio: 'Spent years at Booking.com, including helping build its first AI agents and seeing firsthand how fragile, opaque, and hard to test these systems become at scale. Engineering background at ThoughtWorks before that; full-stack across React, TypeScript, GraphQL, and ML.',
    photo: '/people/rogerio-chaves.png',
    linkedin: 'https://www.linkedin.com/in/rgrchvs/',
    github: 'https://github.com/rogeriochaves',
  },
  {
    slug: 'sergio-cardenas',
    name: 'Sergio Cardenas',
    role: 'AI Agent Expert',
    bio: 'Serial founder who exited his previous startup and was CTO of a fast-growing company. AI agent expert at LangWatch.',
    photo: `${FRAMER}/p6inrWQwd73x14BD4G7hjeRazs.jpeg`,
    linkedin: 'https://www.linkedin.com/in/sergioestebanc/',
    github: 'https://github.com/sergioestebance',
  },
  {
    slug: 'tahmid-tapadar',
    name: 'Tahmid Tapadar',
    role: 'AI Researcher, PhD',
    bio: 'AI researcher with a PhD, focused on agent evaluation and simulation-based testing at LangWatch.',
    photo: `${FRAMER}/HLM4vDStvIk5xOpUCf6Vr1gq8.png`,
    linkedin: 'https://nl.linkedin.com/in/tahmidtapadar',
  },
  {
    slug: 'zhenya',
    name: 'Yevhenii Budnyk',
    role: 'Engineer',
    bio: 'Engineer at LangWatch, writing on retrieval-augmented generation and shipping reliable LLM applications.',
    photo: `${FRAMER}/ncRuBWM3yG0oD2gDfslN9eSbN8.jpeg`,
    linkedin: 'https://www.linkedin.com/in/yevhenii-budnyk/',
    github: 'https://github.com/Eugenumber1',
  },
]

/**
 * Raw frontmatter `author` strings (typos, role suffixes, punctuation
 * variants) mapped to a canonical author slug. Two guest/co-author credits
 * are intentionally left unmapped so they render as plain text instead of a
 * misattributed author page: "CEO of HolidayHero - redated by Manouk" and
 * "Manouk Draisma & FlagSmith" are not a single team member.
 */
const BYLINE_TO_SLUG: Record<string, string> = {
  'Alex Forbes-reed': 'alex-forbes-reed',
  'Alex Forbes-Reed': 'alex-forbes-reed',
  'Alex Fores-Reed': 'alex-forbes-reed',
  'Andrew - Engineer @ LangWatch': 'andrew-joia',
  'Andrew Garde Joia': 'andrew-joia',
  'Andrew Joia': 'andrew-joia',
  'Aryan Sharma': 'aryan-sharma',
  Aryan: 'aryan-sharma',
  'Bram P': 'bram-p',
  'Manouk Draisma': 'manouk-draisma',
  Manouk: 'manouk-draisma',
  Richard: 'richard',
  'Rogerio - CTO': 'rogerio-chaves',
  'Rogerio Chaves - CTO': 'rogerio-chaves',
  'Rogerio Chaves': 'rogerio-chaves',
  Rogerio: 'rogerio-chaves',
  'Sergio Cardenas': 'sergio-cardenas',
  'Tahmid - AI research @LangWatch': 'tahmid-tapadar',
  'Tahmid - AI researcher @ LangWatch': 'tahmid-tapadar',
  'Tahmid AI researcher @ LangWatch': 'tahmid-tapadar',
  'Tahmid AI Researcher @ LangWatch': 'tahmid-tapadar',
  'Tahmid, AI Researcher @ LangWatch': 'tahmid-tapadar',
  'Tahmid Tapadar': 'tahmid-tapadar',
  Zhenya: 'zhenya',
}

export function authorForByline(byline?: string): Author | undefined {
  if (!byline) return undefined
  const slug = BYLINE_TO_SLUG[byline.trim()]
  return slug ? AUTHORS.find((a) => a.slug === slug) : undefined
}

export function getAuthor(slug: string): Author | undefined {
  return AUTHORS.find((a) => a.slug === slug)
}

export function authorSlugPath(author: Pick<Author, 'slug'>): string {
  return `/blog/author/${author.slug}`
}

/** Posts attributed to this author, newest first (blogPosts() is pre-sorted). */
export function postsByAuthor(slug: string) {
  return blogPosts().filter((p) => authorForByline(p.meta.author)?.slug === slug)
}

/** Most recent post that has a photo for this author, if any. */
export function authorPhoto(slug: string): string | undefined {
  return postsByAuthor(slug).find((p) => p.meta.authorPhoto)?.meta.authorPhoto
}

/** Only authors with at least one attributed post get a generated route. */
export function allAuthorsWithPosts(): Author[] {
  return AUTHORS.filter((a) => postsByAuthor(a.slug).length > 0)
}
